import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { NgButton } from './buttons/NgButton';
import { NgCopyButton } from './buttons/NgCopyButton';

export type DataItemProps = {
  title?: string;
  value?: string;
  cssClass?: string;
  allowCopyToClipboard?: boolean;
  showEditButton?: boolean;
  valueToCopy?: string;
  showEmptyPlaceholder?: boolean;
  showEmptyPlaceholderWhen?: () => boolean;
  emptyValuePlaceholder?: string;
  linkUrl?: string;
  children?: ReactNode;
  actionItems?: ReactNode;
  onEditClick?: () => void | Promise<void>;
};

function isNullOrWhiteSpace(value: string | undefined): boolean {
  return value == null || value.trim() === '';
}

export function DataItem({
  title,
  value,
  cssClass,
  allowCopyToClipboard,
  showEditButton,
  valueToCopy,
  showEmptyPlaceholder,
  showEmptyPlaceholderWhen,
  emptyValuePlaceholder = '–',
  linkUrl,
  children,
  actionItems,
  onEditClick,
}: DataItemProps) {
  const { t } = useTranslation();
  const effectiveValueToCopy = valueToCopy ?? value;
  const effectiveShowEmptyPlaceholder =
    (showEmptyPlaceholder === true && isNullOrWhiteSpace(value)) ||
    (showEmptyPlaceholderWhen != null && showEmptyPlaceholderWhen());

  return (
    <div className={`data-item d-flex justify-content-between align-items-center ${cssClass ?? ''}`}>
      <div className="d-flex flex-column flex-grow-1">
        {title != null && <div className="small text-muted text-lowercase">{title}</div>}

        <div className="medium">
          {effectiveShowEmptyPlaceholder ? (
            emptyValuePlaceholder
          ) : linkUrl != null ? (
            <a href={linkUrl}>{value}</a>
          ) : (
            value
          )}
        </div>

        {children}
      </div>

      {actionItems != null && <div>{actionItems}</div>}

      {allowCopyToClipboard && (
        <div>
          <NgCopyButton valueToCopy={effectiveValueToCopy} />
        </div>
      )}

      {showEditButton && (
        <div>
          <NgButton
            icon="fal fa-edit"
            type="link"
            size="small"
            ariaLabel={t('Edit')}
            onClick={onEditClick}
          />
        </div>
      )}
    </div>
  );
}
